import React, {useMemo, useState, memo, useCallback} from "react";
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import {Definition} from "./data_assets_create_view";
import TextFieldWithWarning from "./text_field_with_warning";
import DataAssetSelector from "./data_asset_selector";
import {isDataAsset} from "../containers/data_assets_create_view";
import * as InputEditor from "common_components/flowmaster/input_editors";

import {
  WorkflowInputValues,
  WorkflowInputValue,
  WorkflowInput,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";

const styles = {
  inputsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1.5em",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
  },
  inputName: {
    width: 100,
    paddingRight: "1em",
    fontSize: "0.85rem",
    color: "#424242",
  },
  typeSelector: {
    width: 100,
    marginRight: "1em",
  },
  textField: {
    flex: 1,
  },
  helperText: {
    marginTop: "1em",
  },
} as const;

type InputSource = "literal" | "data_asset";

const filterDefinitionsWithConfigs = (
  definitions: Definition[],
): Definition[] => {
  return definitions.filter(definition => {
    if (
      !definition.workflow_instances ||
      !Array.isArray(definition.workflow_instances)
    ) {
      return false;
    }

    return true;
  });
};

const createInputRowKey = (
  inputId: string,
  inputValue: WorkflowInputValue | DataAssetReference,
  inputSource: string,
) =>
  inputSource === "data_asset" && isDataAsset(inputValue)
    ? `${inputId}-${inputValue.definition_id || ""}-${
        inputValue.config_id || ""
      }`
    : inputId;

export interface DataAssetReference {
  definition_id: string;
  config_id: string;
  run_id: string;
  output_key: string;
  definition_group_id?: string;
  config_group_id?: string;
}

const InputRow = memo(
  ({
    input,
    inputId,
    displayName,
    inputValue,
    inputSource,
    inputChanged,
    filteredDefinitions,
    onTypeChange,
    onValueChange,
    organisationId,
    disabled,
  }: {
    input: WorkflowInput;
    inputId: string;
    displayName: string;
    inputValue: WorkflowInputValue | DataAssetReference;
    inputSource: InputSource;
    inputChanged: boolean;
    filteredDefinitions: Definition[];
    onTypeChange: (inputId: string, type: InputSource) => void;
    onValueChange: (inputId: string, value: WorkflowInputValue) => void;
    organisationId: OrganisationId;
    disabled: boolean;
  }) => {
    const handleTypeChange = useCallback(
      e => {
        if (disabled) {
          return;
        }
        onTypeChange(inputId, e.target.value as InputSource);
      },
      [inputId, onTypeChange],
    );

    const handleValueChange = useCallback(
      e => {
        if (disabled) {
          return;
        }
        onValueChange(inputId, e.target.value);
      },
      [inputId, onValueChange],
    );

    const handleAssetChange = useCallback(
      value => {
        if (disabled) {
          return;
        }
        onValueChange(inputId, value);
      },
      [inputId, onValueChange],
    );

    const shouldHighlight =
      inputSource === "data_asset" &&
      isDataAsset(inputValue) &&
      Boolean(inputValue.definition_id || inputValue.config_id);

    return (
      <div
        style={{
          ...styles.inputRow,
          backgroundColor: shouldHighlight ? "#fff7db" : "default",
        }}
      >
        <div style={styles.inputName}>{displayName}</div>

        <FormControl
          variant="outlined"
          size="small"
          style={styles.typeSelector}
          disabled={disabled}
        >
          <InputLabel>Type</InputLabel>
          <Select
            value={inputSource}
            onChange={handleTypeChange}
            label="Type"
            style={{textTransform: "capitalize"}}
          >
            <MenuItem value="literal">Literal</MenuItem>
            <MenuItem value="data_asset">Asset</MenuItem>
          </Select>
        </FormControl>

        {inputSource === "literal" ? (
          input.type === "string" ? (
            <TextFieldWithWarning
              variant="outlined"
              value={typeof inputValue === "string" ? inputValue : ""}
              onChange={handleValueChange}
              size="small"
              label="Value"
              style={styles.textField}
              warning={inputChanged}
              disabled={disabled}
              multiline
            />
          ) : (
            React.createElement(getInputComponent(input), {
              input,
              value: inputValue,
              fullWidth: true,
              handleInputChange: handleValueChange,
              handleInputBlur: () => {},
              warning: inputChanged,
              key: input.type === "schema" ? `schema-${inputId}` : undefined,
              disabled,
            })
          )
        ) : (
          <DataAssetSelector
            definitions={filteredDefinitions}
            value={inputValue as DataAssetReference}
            onChange={handleAssetChange}
            organisationId={organisationId}
            disabled={disabled}
          />
        )}
      </div>
    );
  },
);

interface ConfigurationInputsProps {
  definitions: Definition[];
  definition?: Definition;
  inputValues: WorkflowInputValues;
  initialInputValues: WorkflowInputValues;
  onInputChange: (
    inputId: string,
    value: WorkflowInputValue | DataAssetReference,
  ) => void;
  organisationId: OrganisationId;
  disabled?: boolean;
}

const ConfigurationInputs: React.FC<ConfigurationInputsProps> = ({
  definitions,
  definition,
  inputValues,
  initialInputValues,
  onInputChange,
  organisationId,
  disabled,
}) => {
  const [inputSources, setInputSources] = useState<Record<string, InputSource>>(
    {},
  );

  const handleTypeChange = useCallback(
    (inputId: string, type: InputSource) => {
      if (disabled) {
        return;
      }
      setInputSources(prev => ({...prev, [inputId]: type}));

      if (type === "literal") {
        onInputChange(inputId, "");
      } else {
        const emptyRef: DataAssetReference = {
          definition_id: "",
          config_id: "",
          run_id: "",
          output_key: "value",
        };
        onInputChange(inputId, emptyRef);
      }
    },
    [onInputChange],
  );

  const filteredDefinitions = useMemo(
    () => filterDefinitionsWithConfigs(definitions),
    [definitions],
  );

  const getInputSource = useCallback(
    (inputId: string, value: WorkflowInputValue): InputSource => {
      if (inputSources[inputId]) {
        return inputSources[inputId];
      }

      if (isDataAsset(value)) {
        return "data_asset";
      }
      return "literal";
    },
    [inputSources],
  );

  if (!definition) {
    return null;
  }

  const definitionInputs = definition.inputs?.values || [];
  if (definitionInputs.length === 0) {
    return (
      <Typography variant="body2" style={styles.helperText}>
        No inputs defined for this definition.
      </Typography>
    );
  }

  return (
    <div style={styles.inputsContainer}>
      {definitionInputs.map(input => {
        const inputId = String(input.id);
        const displayName = input.label || input.name;

        const inputChanged =
          inputValues[inputId] !== initialInputValues[inputId];

        const inputValue = getInputValue(input, inputValues);

        const inputSource = getInputSource(inputId, inputValue);

        return (
          (inputValue || inputValue === "") && (
            <InputRow
              key={createInputRowKey(inputId, inputValue, inputSource)}
              input={input}
              inputId={inputId}
              displayName={displayName}
              inputValue={inputValue}
              inputSource={inputSource}
              inputChanged={inputChanged}
              filteredDefinitions={filteredDefinitions}
              onTypeChange={handleTypeChange}
              onValueChange={onInputChange}
              organisationId={organisationId}
              disabled={Boolean(disabled)}
            />
          )
        );
      })}
    </div>
  );
};

function getInputValue(input, inputValues): WorkflowInputValue {
  const value = inputValues[input.id];

  if (value === undefined) {
    if (input.type === "llm_model") {
      return input.default_value;
    }
    return input.has_default ? input.default_value : "";
  }

  if (input.type === "schema" && typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      return "";
    }
  }

  return value;
}

function getInputComponent(input) {
  return InputEditor[input.type].component;
}

export default ConfigurationInputs;
